import React, { useEffect, useMemo } from "react";
import clsx from "clsx";
import {
  Box,
  Typography,
  Switch,
  makeStyles,
  IconButton
} from "@material-ui/core";
import { connect } from "react-redux";

import EditIcon from "@material-ui/icons/Edit";
import LockIcon from "@material-ui/icons/Lock";
import Checkbox from "@material-ui/core/Checkbox";
import LightTooltip from "../common/LightTooltip";
import OptimizedTooltip from "../common/OptimizedTooltip";

import { secondsToTime, completedStopsCount } from "../../utils/helpers";
import CountIndicatorIcon from "../common/CountIndicatorIcon";
import { FormattedText } from "../common/FormattedText";
import { setExtendableId, setExtendableType } from "../../store/actions/edit";
import { setCurrentVehicleSelection } from "../../store/actions/common";

const useStyles = makeStyles(theme => ({
  vehicle: {
    height: 64,
    borderBottom: theme.variables.border,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(3),
    boxSizing: "border-box",
    cursor: "pointer",
    "&:last-of-type": {
      "& .stop-dots-icon": {
        display: "none"
      }
    },
    "& .edit-remove-btns": {
      opacity: 0,
      transition: "opacity .2s"
    },
    "&:hover": {
      backgroundColor: "#F4F4F4"
    },
    "&:hover .edit-remove-btns": {
      opacity: 1
    },
    "&:hover .pinBox": {
      backgroundColor: "#5f6368"
    },
    "&:hover .icon-time-window": {
      opacity: 1
    }
  },
  switch: {
    "&:not(.MuiSwitch-edgeStart) .Mui-disabled": {
      color: theme.palette.primary.main,
      opacity: 0.38
    },
    "& .Mui-disabled + .MuiSwitch-track": {
      backgroundColor: "rgba(19, 41, 254, 0.5)"
    }
  },
  active: {
    backgroundColor: "#F4F4F4"
  },
  notAllowed: { cursor: theme.cursors.notAllowed },
  summary: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  viewModeDisableVehicles: {
    opacity: 1,
    cursor: "default"
  },
  hideShiftAndRoute: {
    "& .shift-route": {
      opacity: 1,
      transition: "opacity .2s"
    },
    "&:hover .shift-route": {
      opacity: 0
    }
  },
  btnSmall: {
    padding: 8
  },
  checkBox: {
    padding: 8,
    "& .Mui-disabled": {
      cursor: theme.cursors.notAllowed
    }
  },
  vehicleName: {
    fontWeight: "500",
    lineHeight: "1rem",
    fontSize: theme.typography.pxToRem(14),
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingRight: "2px",
    display: "flex",
    alignItems: "center",
    "& svg": {
      color: "#000",
      width: "1rem",
      height: "1rem",
      paddingRight: "6.67px"
    }
    // "& span": {
    //   marginLeft: "6.67px"
    // }
  },
  addressLine: {
    lineHeight: "1rem",
    fontSize: "0.8125rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    color: "#7D7E82",
    marginTop: theme.spacing(0.5),
    paddingRight: theme.spacing(2)
  },
  stopCount: {
    lineHeight: "1rem",
    fontSize: "0.8125rem",
    fontWeight: ({ assigned, tasksCount }) =>
      assigned && tasksCount ? 500 : "normal",
    color: ({ assigned, tasksCount }) =>
      assigned && tasksCount ? "#4D4D50" : "#7D7E82",
    marginTop: theme.spacing(0.5),
    textAlign: "end"
  },
  shiftText: {
    lineHeight: "1rem",
    fontSize: "0.875rem",
    textAlign: "right",
    color: "#7D7E82"
  },
  vehicleInEditPanelStyles: {
    backgroundColor: "#F8F9FF"
  }
}));

const leftBoxWidth = 40;

function Vehicle({
  setCurrentVehicleSelection,
  vehicle,
  vehicleType,
  vehicleDetails,
  editId,
  projectExpired,
  haveSolution,
  configuration,
  selectedVehicles,
  currentVehicleSelection,
  editVehicle,
  handleEnable,
  solution,
  vehicleTypes,
  vehicleFilterText,
  handleChange,
  isViewMode,
  vehicles,
  activeByListCommand,
  setHaveHovered = () => {},
  haveHovered
  // setExtendableId,
  // setExtendableType
}) {
  const { isLocked, assigned, tasksCount, backhaulCount } = vehicleDetails;

  const classes = useStyles({ assigned, tasksCount });
  const isInCurrentSelection = currentVehicleSelection.includes(vehicleType.id);
  const isSelected = vehicle && selectedVehicles.includes(vehicle.vehicle_id);

  const isDisabledEdit = isViewMode || projectExpired;

  const disabledVehicleStartAddress =
    !isSelected &&
    vehicles.find(
      currentVehicle => currentVehicle?.type_id?.id === vehicleType.id
    )?.start_address?.formatted_address;

  const routes = solution ? solution.routes : [];

  const completedStopsCountPerVehicle = useMemo(() => {
    if (isSelected) {
      return completedStopsCount(
        routes.filter(route => route.vehicle_id === vehicle.vehicle_id)[0]
          ?.stops
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, solution]);

  useEffect(() => {
    if (isViewMode || projectExpired) setCurrentVehicleSelection([]);
  }, [isViewMode, projectExpired]);

  const isRouteFinished =
    haveSolution &&
    assigned &&
    completedStopsCountPerVehicle > 0 &&
    completedStopsCountPerVehicle === tasksCount;

  const currentVehicleEditId = vehicle?.vehicle_id
    ? vehicle?.vehicle_id
    : vehicleType?.id;

  const isVehicleEditing =
    editId === "multiple"
      ? isInCurrentSelection
      : editId === currentVehicleEditId;

  // if (isInCurrentSelection && editId && editId !== "multiple") {
  //   setExtendableType("vehicles");
  //   setExtendableId("multiple");
  // }

  return (
    <Box
      position="relative"
      className={clsx({
        [classes.vehicleInEditPanelStyles]: isVehicleEditing,
        [classes.active]: activeByListCommand && !haveHovered
      })}
      onClick={() => {
        currentVehicleEditId && editVehicle(currentVehicleEditId);
      }}
    >
      {isLocked && (
        <LightTooltip
          blackMode
          text={
            "This vehicle cannot be edited as it belongs to a locked route."
          }
        ></LightTooltip>
      )}
      <Box
        display="flex"
        alignItems="center"
        position="relative"
        className={clsx(classes.vehicle, {
          [classes.inCurrentSelection]: isInCurrentSelection,
          // [classes.hideShiftAndRoute]:
          //   isSelected && !isInCurrentSelection && !isDisabledEdit,
          // When we in view mode we should disable vehicle editing
          [classes.viewModeDisableVehicles]: isDisabledEdit
        })}
        data-cy="vehicle"
        mb={0.5}
        // style={{
        //   pointerEvents: isLocked ? "none" : "all"
        // }}
      >
        {/* Left Icon */}
        <OptimizedTooltip
          title={
            isDisabledEdit
              ? `Cannot edit in ${projectExpired ? "completed" : "view"} mode`
              : "" //`${isLocked ? "Select" : ""}`
          }
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={leftBoxWidth}
            id="checkbox"
            onClick={e => e.stopPropagation()}
          >
            {
              <span>
                <Checkbox
                  checked={isInCurrentSelection}
                  onChange={e => {
                    handleChange(vehicleType.id, isInCurrentSelection, e);
                  }}
                  disabled={isDisabledEdit}
                  value="checkedB"
                  color="primary"
                  className={classes.checkBox}
                />
              </span>
            }
          </Box>
        </OptimizedTooltip>
        {/* Vehicle name and details */}
        <Box
          className={clsx(classes.summary, {})}
          style={{
            opacity: isLocked ? 0.5 : 1
          }}
        >
          <OptimizedTooltip
            placement="bottom"
            title={
              !isDisabledEdit
                ? completedStopsCountPerVehicle > 0
                  ? "Unable to disable vehicles which started their route"
                  : isRouteFinished
                  ? "Unable to disable vehicles which completed their route"
                  : isLocked
                  ? "Unable to edit locked vehicles"
                  : !!isSelected
                  ? "Disable"
                  : "Enable"
                : `Cannot edit in ${projectExpired ? "completed" : "view"} mode`
            }
          >
            <span>
              <Switch
                edge={!!isSelected ? "end" : "start"}
                color="primary"
                onChange={() =>
                  handleEnable(vehicleType.id, vehicle && vehicle.vehicle_id)
                }
                onClick={e => e.stopPropagation()}
                checked={!!isSelected}
                defaultChecked={vehicleTypes.length === 1}
                disabled={
                  completedStopsCountPerVehicle > 0 ||
                  isDisabledEdit ||
                  isLocked
                }
                className={clsx(classes.switch, {
                  [classes.notAllowed]:
                    completedStopsCountPerVehicle > 0 || isLocked
                })}
              />
            </span>
          </OptimizedTooltip>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              display="flex"
              flexDirection="column"
              whiteSpace="nowrap"
              maxWidth={200}
              width={200}
              ml={1.5}
            >
              <Typography variant="body1" className={classes.vehicleName}>
                {isLocked && <LockIcon />}
                <span>
                  {vehicleType.name ? (
                    <FormattedText
                      text={vehicleType.name}
                      query={vehicleFilterText}
                    />
                  ) : (
                    <i>Vehicle deleted</i>
                  )}
                </span>
              </Typography>

              <Typography
                variant="body2"
                color="secondary"
                className={classes.addressLine}
              >
                {isSelected &&
                  (configuration.allow_reload && configuration.depot_address
                    ? configuration.depot_address.formatted_address
                    : vehicle.start_address &&
                      vehicle.start_address.formatted_address
                    ? vehicle.start_address.formatted_address
                    : "Start location")}
                {!isSelected &&
                  (configuration.allow_reload && configuration.depot_address
                    ? configuration.depot_address.formatted_address
                    : disabledVehicleStartAddress)}
              </Typography>
            </Box>
            <CountIndicatorIcon
              count={backhaulCount}
              style={{ marginTop: -18, display: "block" }}
            />
            <Box
              display="flex"
              flexDirection="column"
              whiteSpace="nowrap"
              className="shift-route"
              mt={0.25}
            >
              <Typography color="secondary" className={classes.shiftText}>
                {isSelected && vehicle.earliest_start
                  ? `${secondsToTime(vehicle.earliest_start)}-${
                      vehicle.latest_end
                        ? secondsToTime(vehicle.latest_end)
                        : ""
                    }`
                  : "Shift"}
              </Typography>

              {haveSolution && (
                <Typography
                  className={classes.stopCount}
                  color={assigned && tasksCount ? "primary" : "secondary"}
                >
                  {assigned && tasksCount
                    ? `Stops ${completedStopsCountPerVehicle}/${tasksCount}`
                    : "No route"}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        {/* {!isInCurrentSelection && !isDisabledEdit && (
          <Box
            alignSelf="center"
            display="flex"
            alignItems="center"
            className="edit-remove-btns"
            position="absolute"
            my="auto"
            right={16}
          >
            {vehicle && (
              <IconButton
                // component="button"
                onClick={() => editVehicle(vehicle.vehicle_id)}
                className={classes.btnSmall}
                tabIndex="-1"
                data-cy="editVehicle-project"
              >
                <OptimizedTooltip placement="bottom-end" title="Edit vehicle">
                  <EditIcon />
                </OptimizedTooltip>
              </IconButton>
            )}
          </Box>
        )} */}
      </Box>
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    vehicleTypes: state.project.vehicleTypes,
    haveSolution: !!state.project.solution,
    solution: state.project.solution,
    editId: state.extendable.id,
    currentVehicleSelection: state.common.currentVehicleSelection,
    selectedVehicles: state.project.selectedVehicles,
    projectExpired: ["expired", "finished"].includes(
      state.project.status.toLowerCase()
    ),
    configuration: state.project.vrp.configuration,
    isViewMode:
      (state.project.published_once || state.project.published) &&
      !["optimized", "preparing"].includes(state.project.status.toLowerCase()),
    vehicles: state.project.vrp.vehicles
  };
}

export default connect(mapStateToProps, {
  setExtendableId,
  setExtendableType,
  setCurrentVehicleSelection
})(Vehicle);
